// Browser storage can be unusable: Safari private mode and "block all cookies" make merely reading
// `window.localStorage` throw a SecurityError, and some browsers throw QuotaExceededError on every write. Persistence
// is a convenience everywhere in the D2C flow, so every access goes through these helpers, which fall back to "no
// persistence" instead of throwing.

const STORAGE_KINDS = ["localStorage", "sessionStorage"]
const PROBE_KEY = "__hl_storage_probe__"

export function getStorage(kind = "localStorage") {
  try {
    return window[kind] || null
  } catch {
    return null
  }
}

export function safeGetItem(key, kind = "localStorage") {
  try {
    return getStorage(kind)?.getItem(key) ?? null
  } catch {
    return null
  }
}

export function safeSetItem(key, value, kind = "localStorage") {
  try {
    getStorage(kind).setItem(key, value)
    return true
  } catch {
    return false
  }
}

export function safeRemoveItem(key, kind = "localStorage") {
  try {
    getStorage(kind).removeItem(key)
    return true
  } catch {
    return false
  }
}

function createMemoryStorage() {
  const entries = new Map()
  return {
    getItem: (key) => (entries.has(String(key)) ? entries.get(String(key)) : null),
    setItem: (key, value) => {
      entries.set(String(key), String(value))
    },
    removeItem: (key) => {
      entries.delete(String(key))
    },
    clear: () => entries.clear(),
    key: (index) => [...entries.keys()][index] ?? null,
    get length() {
      return entries.size
    },
  }
}

function storageWorks(kind) {
  try {
    const storage = window[kind]
    storage.setItem(PROBE_KEY, "1")
    storage.removeItem(PROBE_KEY)
    return true
  } catch {
    return false
  }
}

// Third-party code we cannot edit (Inertia keeps its history-encryption key in window.sessionStorage and does not
// catch SecurityError) reads window.localStorage / window.sessionStorage directly. When a storage is unusable, swap
// it for an in-memory one so those reads and writes succeed without persisting. Working storage is left alone.
export function installStorageFallback() {
  for (const kind of STORAGE_KINDS) {
    if (storageWorks(kind)) continue

    try {
      Object.defineProperty(window, kind, { configurable: true, value: createMemoryStorage() })
    } catch {
      // Not redefinable: callers of the safe helpers above still degrade gracefully.
    }
  }
}
